import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { post, get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, getCurrentDateString, addDays } from '../utils/dateUtils';
import { config } from '../config';
import './Valet.css';

const Valet = () => {
  const [valetCode, setValetCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [codes, setCodes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getCurrentDateString());
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [description, setDescription] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const { getAuthToken } = useAuth();

  // Requested queue state
  const [requestedQueue, setRequestedQueue] = useState([]);
  const [loadingRequested, setLoadingRequested] = useState(false);
  const [markingReady, setMarkingReady] = useState(null);

  const generateValetCode = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/valet/generate-code',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: {
            description: description,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setValetCode(data);
    } catch (err) {
      console.error('Error generating valet code:', err);
      setError(err.message || 'Failed to generate valet code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearCode = () => {
    setValetCode(null);
    setError(null);
    setDescription('');
    setShowGenerateModal(false);
    fetchCodes();
  };

  const startEditDescription = (code) => {
    setEditingCode(code);
    setEditDescription(code.description || '');
  };

  const cancelEditDescription = () => {
    setEditingCode(null);
    setEditDescription('');
  };

  const saveDescription = async (code) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = await fetch(`${config.API_ENDPOINT}/v1/admin/valet/codes/${code.valet_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: editDescription,
          date: code.date,
        }),
      });

      if (!restOperation.ok) {
        throw new Error('Failed to update description');
      }

      // Update local state
      setCodes(codes.map(c =>
        c.valet_id === code.valet_id
          ? { ...c, description: editDescription }
          : c
      ));

      setEditingCode(null);
      setEditDescription('');
    } catch (err) {
      console.error('Error updating description:', err);
      setError(err.message || 'Failed to update description. Please try again.');
    }
  };

  const fetchRequestedQueue = async () => {
    try {
      setLoadingRequested(true);

      const token = await getAuthToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${config.API_ENDPOINT}/v1/admin/valet/requested`, {
        method: 'GET',
        headers: {
          'Authorization': token,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch requested queue');
      }

      const data = await response.json();
      setRequestedQueue(data.requested || []);
    } catch (err) {
      console.error('Error fetching requested queue:', err);
    } finally {
      setLoadingRequested(false);
    }
  };

  const markVehicleReady = async (item) => {
    try {
      setMarkingReady(item.valet_id);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const response = await fetch(`${config.API_ENDPOINT}/v1/admin/valet/ready`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: item.user_id,
          valet_id: item.valet_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark vehicle as ready');
      }

      // Refresh the requested queue
      await fetchRequestedQueue();
    } catch (err) {
      console.error('Error marking vehicle as ready:', err);
      setError(err.message || 'Failed to mark vehicle as ready. Please try again.');
    } finally {
      setMarkingReady(null);
    }
  };

  const fetchCodes = async () => {
    try {
      setLoadingCodes(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // Query for last 2 days from selected date, plus next day to capture all CST timestamps
      // (Since backend stores dates in UTC, we need 3 UTC dates to cover 2 CST days)
      const dates = [
        addDays(selectedDate, -1), // Previous day
        selectedDate,               // Selected day
        addDays(selectedDate, 1)    // Next day (for late CST times)
      ];

      // Fetch codes for all dates in parallel
      const fetchPromises = dates.map(async (date) => {
        const params = { date };

        if (statusFilter !== 'all') {
          params.status = statusFilter;
        }

        const queryString = new URLSearchParams(params).toString();

        const restOperation = get({
          apiName: 'ChulasAdminAPI',
          path: `/v1/admin/valet/codes?${queryString}`,
          options: {
            headers: {
              Authorization: token,
            },
          },
        });

        const response = await restOperation.response;
        const data = await response.body.json();
        return data.codes || [];
      });

      const results = await Promise.all(fetchPromises);

      // Merge and deduplicate codes from all dates
      const allCodes = results.flat();
      const uniqueCodes = Array.from(
        new Map(allCodes.map(code => [code.valet_id, code])).values()
      );

      // Sort by created_at timestamp (newest first)
      uniqueCodes.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );

      setCodes(uniqueCodes);
    } catch (err) {
      console.error('Error fetching valet codes:', err);
      setError(err.message || 'Failed to fetch valet codes. Please try again.');
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [selectedDate, statusFilter]);

  // Fetch requested queue on mount
  useEffect(() => {
    fetchRequestedQueue();
  }, []);

  // Auto-refresh for both codes table and requested queue every 20 seconds when enabled
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        fetchCodes();
        fetchRequestedQueue();
      }, 10000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, selectedDate, statusFilter]);

  return (
    <div className="valet-container">
      <div className="page-header-section">
        <div>
          <h2>Valet Management</h2>
          <p>Browse and manage valet tickets</p>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="generate-new-button"
        >
          + Generate New Code
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Requested Queue Section */}
      <div className="requested-queue-section">
        <div className="section-header">
          <h3>Requested Queue ({requestedQueue.length})</h3>
          <button
            onClick={fetchRequestedQueue}
            className="refresh-button small"
            disabled={loadingRequested}
          >
            {loadingRequested ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {requestedQueue.length === 0 ? (
          <p className="no-requests">No vehicles currently requested.</p>
        ) : (
          <div className="requested-queue-grid">
            {requestedQueue.map((item, index) => (
              <div key={item.valet_id} className="requested-card">
                <div className="queue-position">#{index + 1}</div>
                <div className="requested-info">
                  <div className="valet-code">{item.valet_id}</div>
                  {item.description && (
                    <div className="vehicle-description">{item.description}</div>
                  )}
                  <div className="customer-info">
                    {item.user_email || item.user_phone || 'No contact info'}
                  </div>
                  <div className="requested-time">
                    Requested: {formatDateTime(item.requested_at)}
                  </div>
                </div>
                <button
                  onClick={() => markVehicleReady(item)}
                  className="mark-ready-button"
                  disabled={markingReady === item.valet_id}
                >
                  {markingReady === item.valet_id ? 'Marking...' : 'Mark Ready'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="codes-list-section">
        <div className="filter-controls">
          <div className="filter-group">
            <label>Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All</option>
              <option value="unclaimed">Unclaimed</option>
              <option value="claimed">Claimed</option>
            </select>
          </div>
          <div className="filter-actions">
            <div className="auto-refresh-toggle">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span className="toggle-label">Auto-refresh</span>
            </div>
            <button
              onClick={fetchCodes}
              className="refresh-button"
              disabled={loadingCodes}
            >
              {loadingCodes ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {loadingCodes ? (
          <div className="loading-spinner">Loading codes...</div>
        ) : (
          <div className="codes-table-container">
            {codes.length === 0 ? (
              <p className="no-codes">No codes found for the selected date and status.</p>
            ) : (
              <table className="codes-table">
                <thead>
                  <tr>
                    <th>Valet Code</th>
                    <th>Status</th>
                    <th>Description</th>
                    <th>Generated At</th>
                    <th>Generated By</th>
                    <th>Claimed At</th>
                    <th>Claimed By</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => (
                    <tr key={code.valet_id}>
                      <td className="code-id">{code.valet_id}</td>
                      <td>
                        <span className={`status-badge ${code.status}`}>
                          {code.status}
                        </span>
                      </td>
                      <td className="description-cell">
                        {editingCode?.valet_id === code.valet_id ? (
                          <div className="edit-description-container">
                            <input
                              type="text"
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Enter description..."
                              className="description-input"
                              maxLength={100}
                            />
                            <div className="edit-actions">
                              <button
                                onClick={() => saveDescription(code)}
                                className="save-btn"
                                title="Save"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelEditDescription}
                                className="cancel-btn"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="description-display">
                            <span className="description-text">
                              {code.description || '-'}
                            </span>
                            <button
                              onClick={() => startEditDescription(code)}
                              className="edit-btn"
                              title="Edit description"
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </td>
                      <td>{formatDateTime(code.created_at)}</td>
                      <td>{code.generated_by_email || code.generated_by_phone || 'N/A'}</td>
                      <td>{code.claimed_at ? formatDateTime(code.claimed_at) : '-'}</td>
                      <td>{code.claimed_by_email || code.claimed_by_phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="codes-count">
              Total: <strong>{codes.length}</strong> code{codes.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowGenerateModal(false)}>
              ×
            </button>

            {!valetCode ? (
              <div className="modal-generate-section">
                <div className="generate-icon">🎫</div>
                <h3>Generate Valet Code</h3>
                <p>Create a unique QR code for valet service. Customers can scan this code to associate their vehicle with their account.</p>
                <div className="form-group">
                  <label htmlFor="description">Description (optional):</label>
                  <input
                    id="description"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Red Toyota Camry"
                    className="description-input"
                    maxLength={100}
                    disabled={loading}
                  />
                  <small className="help-text">Add a note to identify this valet ticket</small>
                </div>
                <button
                  onClick={generateValetCode}
                  className="generate-button"
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Generate Valet Code'}
                </button>
              </div>
            ) : (
              <div className="modal-code-display">
                <h3>Valet Code Generated Successfully</h3>

                <div className="qr-code-container">
                  <QRCodeSVG
                    value={valetCode.deepLinkUrl || valetCode.valetCode}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                  <p className="qr-helper-text">Scan to open valet in app</p>
                </div>

                <div className="code-details">
                  <div className="code-item">
                    <span className="code-label">Valet Code:</span>
                    <span className="code-value">{valetCode.valetCode}</span>
                  </div>

                  {valetCode.deepLinkUrl && (
                    <div className="code-item">
                      <span className="code-label">Deep Link URL:</span>
                      <span className="code-value code-url">{valetCode.deepLinkUrl}</span>
                    </div>
                  )}

                  {valetCode.userEmail && (
                    <div className="code-item">
                      <span className="code-label">User Email:</span>
                      <span className="code-value">{valetCode.userEmail}</span>
                    </div>
                  )}

                  {valetCode.userPhone && (
                    <div className="code-item">
                      <span className="code-label">User Phone:</span>
                      <span className="code-value">{valetCode.userPhone}</span>
                    </div>
                  )}

                  <div className="code-item">
                    <span className="code-label">Generated:</span>
                    <span className="code-value">
                      {formatDateTime(valetCode.generatedAt)}
                    </span>
                  </div>

                  <div className="code-item">
                    <span className="code-label">Expires In:</span>
                    <span className="code-value">{valetCode.expiresIn / 60} minutes</span>
                  </div>
                </div>

                <div className="code-actions">
                  <button onClick={clearCode} className="new-code-button">
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Valet;
